// Spawn-time placement: per-seed rejection sampling. Each candidate's
// padding around UI rects scales with its own depth so foreground
// bubbles (rendered z-index 11+, in front of the UI) demand a wide
// exclusion zone while background haze can sit close to content
// without visual harm. If a near bubble exhausts its budget, fall to
// the bottom edge — never random, since random can land on the logo.

import type { Rect, Vec2 } from './bubble-physics';
import { rectsOverlap } from './bubble-anchors';

const MAX_TRIES_PER_SEED = 150;
const MIN_SPACING_FACTOR = 1.15;
const ANCHOR_PAD_BASE_PX = 12;
const ANCHOR_PAD_DEPTH_PX = 16;

type Seed = { depth: number };

type SpawnArgs = {
  width: number;
  height: number;
  size: number;
  rects: Rect[];
  seeds: Seed[];
};

type Bounds = { maxX: number; maxY: number; minDist: number };
type Ctx = { args: SpawnArgs; bounds: Bounds; placed: Vec2[] };

function tooClose(p: Vec2, others: Vec2[], minDist: number): boolean {
  for (let i = 0; i < others.length; i++) {
    const o = others[i];
    if (!o) continue;
    const dx = p.x - o.x;
    const dy = p.y - o.y;
    if (dx * dx + dy * dy < minDist * minDist) return true;
  }
  return false;
}

function padRect(r: Rect, p: number): Rect {
  return { x: r.x - p, y: r.y - p, w: r.w + 2 * p, h: r.h + 2 * p };
}

function rectsForDepth(rects: Rect[], depth: number): Rect[] {
  const pad = ANCHOR_PAD_BASE_PX + ANCHOR_PAD_DEPTH_PX * depth;
  return rects.map((r) => padRect(r, pad));
}

function trySample(ctx: Ctx, padded: Rect[]): Vec2 | null {
  const x = Math.random() * ctx.bounds.maxX;
  const y = Math.random() * ctx.bounds.maxY;
  if (rectsOverlap({ x, y, size: ctx.args.size }, padded)) return null;
  if (tooClose({ x, y }, ctx.placed, ctx.bounds.minDist)) return null;
  return { x, y };
}

function placeOne(ctx: Ctx, depth: number): Vec2 {
  const padded = rectsForDepth(ctx.args.rects, depth);
  for (let i = 0; i < MAX_TRIES_PER_SEED; i++) {
    const p = trySample(ctx, padded);
    if (p) return p;
  }
  // Last resort: random in viewport. Runtime uiAvoid (depth-scaled,
  // up to 2x for foreground) will steer the bubble off any anchor it
  // happened to land on within a few frames — never cluster.
  return {
    x: Math.random() * ctx.bounds.maxX,
    y: Math.random() * ctx.bounds.maxY,
  };
}

export function spawnPositions(args: SpawnArgs): Vec2[] {
  const ctx: Ctx = {
    args,
    bounds: {
      maxX: args.width - args.size,
      maxY: args.height - args.size,
      minDist: args.size * MIN_SPACING_FACTOR,
    },
    placed: [],
  };
  for (let i = 0; i < args.seeds.length; i++) {
    const seed = args.seeds[i];
    if (!seed) continue;
    ctx.placed.push(placeOne(ctx, seed.depth));
  }
  return ctx.placed;
}
